import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import LoadPrepaidCardModal from 'components/load-prepaid-card-modal';
import SelectPaymentMethod from 'components/payment-method-select';
import TextButton from 'components/text-button';
import { TextWithArrowButton } from 'components/text-with-arrow-button';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { useNetworkContext } from 'state/network';
import { usePaymentContext } from 'state/payment';
import { PREPAID_RELOAD_AMOUNTS } from 'state/payment/constants';
import {
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  IPaymentMethod,
} from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { getStoredPrepaidCard } from 'utils/encryption';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { isAllowedReloadMethod } from 'utils/payment';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';

import { Container, FullLineSeparator, TextButtonsWrapper } from '../styled';

import Header from './Header';
import {
  ActionContainer,
  AmountContainer,
  ContentBlock,
  FinalActionContainer,
  LoadPrePaidDialogContainer,
  MoneyButton,
  NoConnectionHeading,
  SelectPaymentMethodWrapper,
  TimCardHeading,
  TimCardSubHeading,
} from './styled';
import TimCardScanSection from './tim-card-scan-section';
import { ILoyalty } from './types';

const INITIAL_LOAD_AMOUNT = 500;

const LoyaltyTimCard: FC<ILoyalty> = ({ onDismiss }) => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    getBalanceFromPaymentMethods,
    paymentMethods,
    reloadPrepaidCard,
    loading,
    prepaidReloadPaymentMethodId: reloadPaymentMethodId,
    setPrepaidReloadPaymentMethodId: setReloadPaymentMethodId,
    openTransferBalanceDialog,
    getPaymentMethods,
  } = usePaymentContext();
  const auth = useAuthContext();
  const { formatCurrencyForUser } = useUIContext();
  const billingCountry = delv(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { user } = useAuthContext();
  const { connection } = useNetworkContext();
  const [reloadAmt, setReloadAmt] = useState(
    user?.details?.defaultReloadAmt || INITIAL_LOAD_AMOUNT
  );
  const [currentTotalTimBalance, setCurrentTotalTimBalance] = useState(INITIAL_LOAD_AMOUNT);
  const [forceSuccessDialog, setForceSuccessDialog] = useState(false);
  const navigate = useNavigate();

  // Load payment methods on unmount to avoid changing balance
  // while the user looks at it or pickup new balance if the
  // user just paid at the store
  useEffectOnUnmount(() => {
    getPaymentMethods();
  });

  const allowedReloadMethods = useMemo(() => paymentMethods.filter(isAllowedReloadMethod), [
    paymentMethods,
  ]);

  // Makes sure that saved reloadPaymentMethodId exists in currently saved reloadMethods
  const hasAvailableReloadMethod = useMemo(
    () =>
      allowedReloadMethods.find(
        ({ accountIdentifier, fdAccountId }) =>
          fdAccountId === reloadPaymentMethodId || accountIdentifier === reloadPaymentMethodId
      ),
    [allowedReloadMethods, reloadPaymentMethodId]
  );

  useEffect(() => {
    if (!allowedReloadMethods.length) {
      return;
    }

    // If no default reloadPaymentMethod available, set default to next available
    if (!hasAvailableReloadMethod) {
      const accountIdentifier =
        allowedReloadMethods[0].fdAccountId ?? (allowedReloadMethods[0].accountIdentifier || '');
      setReloadPaymentMethodId(accountIdentifier);
    }
  }, [allowedReloadMethods, hasAvailableReloadMethod, setReloadPaymentMethodId]);

  const [LoadPrepaidCardDialog, openLoadPrepaidCardDialog] = useDialogModal<
    {},
    { loadAmount: number; forceSuccess?: boolean }
  >({
    Component: LoadPrepaidCardModal,
  });

  const prepaidPaymentMethod = getPrepaidPaymentMethod();

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  let balance = '0.00';
  let centsBalance = 0;

  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    centsBalance = getBalanceFromPaymentMethods(prepaidPaymentMethod!);
    balance = centsToDollarString(centsBalance);
  }

  const reloadGiftCard = useCallback(
    async (input?: IApplePayDetails | IGooglePayDetails) => {
      const giftCard = paymentMethods.find((method: IPaymentMethod) => !!method.prepaid);
      const paymentCard = paymentMethods.find(
        (method: IPaymentMethod) => method.fdAccountId === reloadPaymentMethodId
      );
      const prepaidFdAccountId = giftCard?.fdAccountId ?? null;
      const cardType = paymentCard?.credit?.cardType ?? null;
      const ccReloadDetails = {
        fdAccountId: reloadPaymentMethodId,
      };

      let args = {
        fundValue: reloadAmt,
        fdAccountId: reloadPaymentMethodId,
        prepaidBalance: centsBalance,
        countryCode,
        currencyCode,
        cardType,
        ...(prepaidFdAccountId && { prepaidFdAccountId }),
      };

      switch (reloadPaymentMethodId) {
        case CardTypes.APPLE_PAY:
          args = { ...args, ...{ applePayDetails: input } };
          break;
        case CardTypes.GOOGLE_PAY:
          args = { ...args, ...{ googlePayDetails: input } };
          break;
        default:
          args = { ...args, ...ccReloadDetails };
      }

      const reloadResult = await reloadPrepaidCard(args);

      if (typeof reloadResult === 'number') {
        setCurrentTotalTimBalance(reloadResult);
        setForceSuccessDialog(true);
        openLoadPrepaidCardDialog();
      }
    },
    [
      centsBalance,
      countryCode,
      currencyCode,
      setCurrentTotalTimBalance,
      setForceSuccessDialog,
      openLoadPrepaidCardDialog,
      paymentMethods,
      reloadAmt,
      reloadPaymentMethodId,
      reloadPrepaidCard,
    ]
  );

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      reloadGiftCard(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onCompleted: (data: IGooglePayDetails) => {
      if (data) {
        reloadGiftCard(data);
      }
    },
  });

  const handleReloadClick = useCallback(() => {
    if (applePaySelected) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
      });
    }
    if (googlePaySelected) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: reloadAmt,
        gatewayMerchantId,
        gateway,
      });
    }
    return reloadGiftCard();
  }, [
    applePaySelected,
    googlePaySelected,
    reloadGiftCard,
    requestApplePayPayment,
    requestGooglePayPayment,
    countryCode,
    currencyCode,
    reloadAmt,
  ]);

  const handleNavigate = useCallback(
    (e, route) => {
      navigate(route, { replace: true });
      onDismiss(e);
    },
    [navigate, onDismiss]
  );

  const nonSelectedPaymentMethods = allowedReloadMethods.filter(
    (method: IPaymentMethod) => method.fdAccountId !== reloadPaymentMethodId
  );

  const selectedCreditMethod = allowedReloadMethods.find(
    (method: IPaymentMethod) => method.fdAccountId === reloadPaymentMethodId
  );

  const reloadDisabled =
    !prepaidPaymentMethod ||
    !reloadAmt ||
    (!selectedCreditMethod && !applePaySelected && !googlePaySelected);

  const cachedPrepaidCard = getStoredPrepaidCard();

  const useLoadPrePaidDialog = !loading && !(prepaidPaymentMethod && prepaidPaymentMethod.prepaid);

  if (!connection.connected) {
    return (
      <>
        <NoConnectionHeading>
          {formatMessage({ id: 'unableToLoadTimCardNoInternet' })}
        </NoConnectionHeading>
        {cachedPrepaidCard && <TimCardScanSection barcode={cachedPrepaidCard} />}
      </>
    );
  }

  return (
    <>
      <LoadPrepaidCardDialog
        loadAmount={currentTotalTimBalance}
        forceSuccess={forceSuccessDialog}
      />

      {useLoadPrePaidDialog ? (
        <LoadPrePaidDialogContainer>
          <ContentBlock margin="6.5rem 0 0">
            <TimCardHeading>{formatMessage({ id: 'timCard' })}</TimCardHeading>
            <TimCardSubHeading>{formatMessage({ id: 'emptyStateTimCardCopy' })}</TimCardSubHeading>
            <ActionContainer>
              <ActionButton
                variant={ActionButtonVariants.INVERSE}
                fullWidth
                onClick={openLoadPrepaidCardDialog}
              >
                {formatMessage({ id: 'emptyStateTimCTACopy' })}
              </ActionButton>
              <TextButton onClick={openTransferBalanceDialog}>
                {formatMessage({ id: 'transferBalanceFromAnotherTimCard' })}
              </TextButton>
            </ActionContainer>
          </ContentBlock>
        </LoadPrePaidDialogContainer>
      ) : (
        <Container>
          <ContentBlock margin={`${primitive.$spacing2} 0 0`}>
            <Header balance={balance} />
            <FullLineSeparator margin={`0 -${primitive.$spacing5} ${primitive.$spacing6}`} />
            <TimCardScanSection
              barcode={prepaidPaymentMethod?.prepaid?.shuffledCardNumber ?? null}
            />
          </ContentBlock>

          <ContentBlock>
            <TimCardHeading>{formatMessage({ id: 'reloadYourCard' })}</TimCardHeading>
            {(!!allowedReloadMethods.length || selectedCreditMethod) && (
              <AmountContainer>
                {PREPAID_RELOAD_AMOUNTS.map((amount: number) => (
                  <MoneyButton
                    key={amount}
                    aria-label={formatMessage(
                      { id: 'reloadCardWithAmount' },
                      {
                        amount: formatCurrencyForUser(amount),
                      }
                    )}
                    selected={reloadAmt === amount}
                    onClick={() => setReloadAmt(amount)}
                    aria-current={reloadAmt === amount}
                  >
                    <div>${centsToDollarString(amount, 0)}</div>
                  </MoneyButton>
                ))}
              </AmountContainer>
            )}
            <SelectPaymentMethodWrapper>
              <SelectPaymentMethod
                selectedMethod={selectedCreditMethod}
                nonSelectedMethods={nonSelectedPaymentMethods}
                onOptionClick={setReloadPaymentMethodId}
                onNavigateClick={handleNavigate}
              />
            </SelectPaymentMethodWrapper>
            <FinalActionContainer>
              <ActionButton
                variant={ActionButtonVariants.PRIMARY}
                fullWidth
                onClick={handleReloadClick}
                isLoading={loading}
                disabled={reloadDisabled}
                loadingColor={Styles.color.primary}
                skipLogEvent
              >
                {formatMessage({ id: 'reload' })}
              </ActionButton>
            </FinalActionContainer>
            <TextButtonsWrapper>
              <FullLineSeparator margin={`0`} />
              <TextWithArrowButton
                onClick={() => handleNavigate('', routes.timCard)}
                messageId="myTimCardLink"
              />
              <FullLineSeparator margin={`0`} />
              <TextWithArrowButton
                onClick={openTransferBalanceDialog}
                messageId="transferBalance"
              />
              <FullLineSeparator margin={`0`} />
            </TextButtonsWrapper>
          </ContentBlock>
        </Container>
      )}
    </>
  );
};

export default LoyaltyTimCard;
